import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../lib/firebase';
import './userDetails.css';
import { FaPlusCircle, FaSyncAlt, FaArrowLeft, FaTimes } from 'react-icons/fa';
import { dummy } from '../assets';
import CryptoMarquee from '../Chart/CryptoMarquee';
import Loader from '../loader/Loader';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { format } from 'date-fns';
import UserKYC from './UserKYC';




const UserInfo = () => {
  const { userId } = useParams(); // Get the userId from the URL parameters
  const navigate = useNavigate(); // Initialize useNavigate
  const [user, setUser] = useState(null); // State to store user data
  const [loading, setLoading] = useState(true); // State to manage loading state
  const [editMode, setEditMode] = useState(false); // State to toggle edit mode
  const [updatedUser, setUpdatedUser] = useState({}); // State to store updated user data
  const [successMessage, setSuccessMessage] = useState(''); // State to store success messages
  const [profilePictureUrl, setProfilePictureUrl] = useState(''); // State to store profile picture URL
  const [uploadError, setUploadError] = useState(''); // State to store upload errors
  const [balance, setBalance] = useState(0); // State to store the balance
  const [totalCredit, setTotalCredit] = useState(0); // State to store total credit
  const [totalDebit, setTotalDebit] = useState(0); // State to store total debit
  const fileInputRef = useRef(null); // Create a reference for the file input
  const [country, setCountry] = useState(null);
  const options = countryList().getData();
  const [isOpen, setIsOpen] = useState(false);




  // Function to toggle the box visibility
  const toggleBox = () => {
    setIsOpen(!isOpen);
  };


  const changeHandler = value => {
    setCountry(value);
    setUpdatedUser({ ...updatedUser, country: value.label });
  };

  useEffect(() => {
    // Function to fetch user data from Firestore
    const fetchUser = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser(userData);
          setUpdatedUser(userData);
          setProfilePictureUrl(userData.profilePictureUrl || '');

          // Calculate the balance, total credit, and total debit
          const userTransactions = userData.transactions || [];
          const userBalance = userTransactions.reduce((total, transaction) => {
            if (transaction.type === 'credit') {
              total += transaction.amount;
            } else if (transaction.type === 'debit') {
              total -= transaction.amount;
            }
            return total;
          }, 0);

          const credits = userTransactions.filter(transaction => transaction.type === 'credit');
          const debits = userTransactions.filter(transaction => transaction.type === 'debit');

          const totalCredits = credits.reduce((sum, transaction) => sum + transaction.amount, 0);
          const totalDebits = debits.reduce((sum, transaction) => sum + transaction.amount, 0);

          setBalance(userBalance); // Set the balance
          setTotalCredit(totalCredits); // Set total credit
          setTotalDebit(totalDebits); // Set total debit

        } else {
          console.log('No such document!');
        }
        setLoading(false); // Set loading state to false
      } catch (error) {
        console.error('Error fetching user: ', error);
        setLoading(false); // Set loading state to false in case of error
      }
    };

    fetchUser();
  }, [userId]); // Fetch user data when userId changes

  // Change value on input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser({ ...updatedUser, [name]: value }); // Update user data state when input changes
  };

  // Format date if user.dob is available
  const formattedDob = user?.dob ? format(new Date(user.dob), 'dd MMMM, yyyy') : '';


  const handleSave = async () => {
    try {
      await updateDoc(doc(db, 'users', userId), updatedUser); // Update user document in Firestore
      setUser(updatedUser); // Set user state to updated data
      setEditMode(false); // Exit edit mode
  
      // Reload the page immediately after the update
      window.location.reload();
    } catch (error) {
      console.error('Error updating user: ', error);
    }
  };


  const handleEditMode = async () => {
    setEditMode(true);
    await fetchUser();  // Refresh user data
  };
  
  const fetchUser = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser(userData);  // Set the user data
        setUpdatedUser(userData); // Ensure the updated user state is set
      } else {
        console.log('No such document!');
      }
    } catch (error) {
      console.error('Error fetching user: ', error);
    } finally {
      setLoading(false);
    }
  };
  
  



  // Handle forgot password
  const handleForgotPassword = async () => {
    const auth = getAuth();

    if (!user || !user.email) {
      setUploadError('User email not found.'); // Set error if user email is not found
      return;
    }

    try {
      await sendPasswordResetEmail(auth, user.email); // Send password reset email
      setSuccessMessage('Password reset email sent successfully to ' + user.email); // Set success message
      setTimeout(() => {
        setSuccessMessage(''); // Clear success message after 8 seconds
      }, 8000);
    } catch (error) {
      setUploadError(`Error sending password reset email: ${error.message}`); // Set error message
      console.error('Error sending password reset email:', error.message);
    }
  };

  // Handle file change for profile picture upload
  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      handleUpload(e.target.files[0]); // Automatically upload the selected file
    }
  };

  // Handle upload of profile picture
  const handleUpload = async (file) => {
    const storage = getStorage();
    const storageRef = ref(storage, `profilePictures/${userId}/${file.name}`); // Create storage reference

    try {
      const snapshot = await uploadBytes(storageRef, file); // Upload profile picture
      const downloadURL = await getDownloadURL(snapshot.ref); // Get download URL

      // Update the Firestore document with the new profile picture URL
      await updateDoc(doc(db, 'users', userId), { profilePictureUrl: downloadURL });
      setProfilePictureUrl(downloadURL); // Set profile picture URL state

      setSuccessMessage('Profile picture uploaded successfully.'); // Set success message
      setTimeout(() => {
        setSuccessMessage(''); // Clear success message after 3 seconds
      }, 3000);
    } catch (error) {
      setUploadError(`Error uploading profile picture: ${error.message}`); // Set error message
      console.error('Error uploading profile picture:', error.message);
    }
  };

  const handleGoBack = () => {
    navigate(-1); // Navigate back to the previous page
  };

  if (loading) {
    return <Loader />; // Show loading message if data is still loading
  }

  if (!user) {
    return <div className='pageCover'>User not found</div>; // Show message if user data is not found
  }

  return (
    <div className="userPage pageCover">
      <div>
        <FaArrowLeft onClick={handleGoBack} className="reset" />
      </div>

      <CryptoMarquee />

      {/* Success message for password reset */}
      {successMessage && <p className="success-message">{successMessage}</p>}
      {uploadError && <p className="error-message">{uploadError}</p>}


      {/* Intro User Information */}
      <div className="containers">
        <div className="row">
          {/* Profile Image Section */}
          <div className="col-12 col-md-4">
            <div className='dpCover'>
              <div className='profile-picture-container'>
                <div className='profile-picture-wrapper'>
                  <img
                    src={profilePictureUrl || dummy}
                    alt="Profile"
                    className="profile-picture"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className="file-input"
                    style={{ display: 'none' }}
                  />
                  <FaPlusCircle
                    className="upload-icon"
                    onClick={() => fileInputRef.current.click()}
                  />
                </div>
                <div className='centerBox head'>
                  <>{user.fullName}</>
                </div>
                <div className='centerBox'> <strong>Current Plan: {user.plan}</strong></div>
              </div>

              <div>
                <div className="balanceBox"><strong>Total Balance: ${balance.toFixed(2)}</strong></div>
                <div className="balanceBox success-credit"><strong>Total Credit: ${totalCredit.toFixed(2)}</strong></div>
                <div className="balanceBox error-debit"><strong>Total Debit: ${totalDebit.toFixed(2)}</strong></div>
                <div className='center'> <Link className='link' to={`/user/${userId}/transactions`}>View Transaction Log</Link></div>

              </div>
            </div>
          </div>


          {/* Second Column for Basic Info */}
          <div className="col-12 col-md-8">
            <div className='container'>

              {/* two rows for full name and gender */}
              <div className="row infoCover">
                <div className="col-md-6">
                  <strong>Full Name: </strong>
                  {editMode ? (
                    <input
                      type="text"
                      name="fullName"
                      value={updatedUser.fullName}
                      onChange={handleChange}
                    />
                  ) : (
                    <>{user.fullName}</>
                  )}
                  <hr />
                </div>
                <div className="col-md-6">
                  <strong>Gender: </strong>
                  {editMode ? (
                    <select
                      name="gender"
                      value={updatedUser.gender}
                      onChange={handleChange}
                    >
                      <option value="male">male</option>
                      <option value="female">female</option>
                    </select>
                  ) : (
                    <>{user.gender}</>
                  )}
                  <hr />
                </div>
              </div>

              {/* two rows for plans and roles */}
              <div className="row infoCover">
                <div className="col-md-6">
                  <strong>User Plan: </strong>
                  {editMode ? (
                    <select
                      name="plan"
                      value={updatedUser.plan}
                      onChange={handleChange}
                    >
                      <option value="">- Select User Plan -</option>
                      <option value="basic">Basic</option>
                      <option value="premium">Premium</option>
                      <option value="professional">Professional</option>
                    </select>
                  ) : (
                    <>{user.plan}</>
                  )}
                  <hr />
                </div>
                <div className="col-md-6">
                  <strong>User Role: </strong>
                  {editMode ? (
                    <select
                      name="role"
                      value={updatedUser.role}
                      onChange={handleChange}
                    >
                      <option value="">- Select User Role -</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                      <option value="investor">Investor</option>
                    </select>
                  ) : (
                    <>{user.role}</>
                  )}
                  <hr />
                </div>
              </div>

              {/* single row for address */}
              <div className="row infoCover">
                <div className="col-md-12">
                  <strong>Home Address: </strong>
                  {editMode ? (
                    <input
                      type="text"
                      name="address"
                      value={updatedUser.address}
                      onChange={handleChange}
                    />
                  ) : (
                    <>{user.address}</>
                  )}
                  <hr />
                </div>
              </div>


              {/* two rows for country and phone */}
              <div className="row infoCover">
                <div className="col-md-6">
                  <strong>Country: </strong>
                  {editMode ? (
                    <Select
                      className='select'
                      options={options}
                      value={options.find(option => option.value === country)} // Setting the current value
                      onChange={changeHandler}
                      placeholder={user.country || 'Select Country'}
                    />
                  ) : (
                    <>{user.country}</>
                  )}
                  <hr />
                </div>
                <div className="col-md-6">
                  <strong>Phone Number: </strong>
                  {editMode ? (
                    <PhoneInput
                      country={'us'}
                      value={updatedUser.phone} // Use updatedUser.phone for editing
                      onChange={(value) => setUpdatedUser({ ...updatedUser, phone: value })} // Update phone in updatedUser state
                      className="select"
                      inputProps={{ name: 'phone', required: true }}
                    />
                  ) : (
                    user.phone 
                  )}
                  <hr />

                </div>
              </div>



              {/* two rows for date of birth and phone number */}
              <div className="row infoCover">
                <div className="col-md-6">
                  <strong>Date of Birth: </strong>
                  {editMode ? (
                    <input
                      type="date"
                      name="dob"
                      value={updatedUser.dob}
                      onChange={handleChange}
                    />
                  ) : (
                    <>{formattedDob}</>
                  )}
                  <hr />
                </div>
                <div className="col-md-6">

                  <strong>Email: </strong>
                  {editMode ? (
                    <input
                      type="text"
                      name="email"
                      value={updatedUser.email}
                      onChange={handleChange}
                      readOnly={editMode} // Set readOnly based on editMode

                    />
                  ) : (
                    // send reset password to email for user
                    <>{user.email} <FaSyncAlt onClick={handleForgotPassword} className="reset" /></>
                  )}
                  <hr />
                </div>
              </div>



              {/* two rows for code and group */}
              <div className="row infoCover">
                <div className="col-md-6">
                  <strong>Special Code: </strong>
                  {editMode ? (
                    <input
                      type="text"
                      name="code"
                      value={updatedUser.code}
                      onChange={handleChange}
                    />
                  ) : (
                    <>********</>
                  )}
                  <hr />
                </div>
                <div className="col-md-6">
                  <strong>Status Group: </strong>
                  {editMode ? (
                    <select
                      name="group"
                      value={updatedUser.group}
                      onChange={handleChange}
                    >
                      <option value="">- Select User Role -</option>
                      <option value="activated">activated</option>
                      <option value="deactivated">deactivated</option>
                    </select>
                  ) : (
                    <>{user.group}</>
                  )}
                  <hr />
                </div>
              </div>

              {/* single row for report */}
              <div className="row infoCover">
                <div className="col-md-12">
                  <strong>Transaction Report: </strong>
                  {editMode ? (
                    <input
                      type="text"
                      name="report"
                      value={updatedUser.report}
                      onChange={handleChange}
                    />
                  ) : (
                    <>{user.report}</>
                  )}
                  <hr />
                </div>
              </div>
            </div>


            <div className="button-container">
              {editMode ? (
                // * Display if User is Editing
                <button onClick={handleSave} className='editButton'>Save</button>
              ) : (
                // * else display to edit
                <button onClick={handleEditMode} className='editButton'>Edit</button>
              )}


              <Link to={`/kyc/${userId}`}>
                <button className="kycButton">KYC Documents</button>
              </Link>


              {/* Update User Box in a Modal */}
              <div className="boxContainer">
                <button className="kycButton" onClick={toggleBox}>
                  Update KYC
                </button>

                {isOpen && (

                  <>
                    <div className="box">
                      <UserKYC />
                      <button className="close-btn" onClick={toggleBox}>
                        <FaTimes className="" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>


        </div>
      </div>
      <div>
      </div>


    </div>
  );
};

export default UserInfo;
